import React, { useEffect, useState } from 'react';
import particularesApiService from '../services/ParticularesApiService';
import ParticularModal from './common/ParticularModal';

// Código de matriculación de un curso: el docente lo genera, lo comparte y los alumnos se matriculan con él
// desde "Cursos". El código se guarda cifrado; solo el docente puede verlo.

const getMatriculacion = (response) => {
  const data = response?.matriculacion || response?.Matriculacion || {};
  return {
    activa: Boolean(data.activa ?? data.Activa),
    codigo: data.codigo ?? data.Codigo ?? '',
    generadoUtc: data.generadoUtc ?? data.GeneradoUtc ?? null
  };
};

const formatFecha = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ParticularCursoMatriculacionModal = ({ curso, cursoNombre, onClose, onChanged }) => {
  const cursoId = curso?.id || curso?.Id;
  const [matriculacion, setMatriculacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [confirmRegenerar, setConfirmRegenerar] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await particularesApiService.getCursoMatriculacion(cursoId);
        if (active) setMatriculacion(getMatriculacion(response));
      } catch (err) {
        if (active) setError(err.message || 'No pudimos cargar la matriculación del curso.');
      } finally {
        if (active) setLoading(false);
      }
    };
    if (cursoId) load();
    return () => { active = false; };
  }, [cursoId]);

  const run = async (action) => {
    setSaving(true);
    setError('');
    try {
      const response = await action();
      setMatriculacion(getMatriculacion(response));
      setConfirmRegenerar(false);
      setCopied(false);
      onChanged?.();
    } catch (err) {
      setError(err.message || 'No pudimos actualizar la matriculación.');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerar = () => run(() => particularesApiService.generarCodigoMatriculacion(cursoId));
  const handleToggle = () => run(() => particularesApiService.setCursoMatriculacion(cursoId, !matriculacion?.activa));

  const handleCopy = async () => {
    if (!matriculacion?.codigo) return;
    try {
      await navigator.clipboard.writeText(matriculacion.codigo);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('No se pudo copiar el código. Seleccionalo y copialo a mano.');
    }
  };

  const tieneCodigo = Boolean(matriculacion?.codigo);

  return (
    <ParticularModal title="Matriculación por código" icon="fas fa-key" onClose={saving ? () => {} : onClose}>
      <div className="particular-modal-body">
        <p className="particular-matricula-intro">
          Compartí este código con tus alumnos. Lo buscan en <strong>Cursos</strong>, encuentran <strong>{cursoNombre}</strong> y se matriculan
          ingresándolo. También podés seguir inscribiéndolos a mano.
        </p>

        {loading && <p className="particular-matricula-muted"><i className="fas fa-spinner fa-spin" /> Cargando...</p>}

        {!loading && matriculacion && (
          <>
            <div className={`particular-matricula-status ${matriculacion.activa ? 'open' : 'closed'}`}>
              <i className={`fas ${matriculacion.activa ? 'fa-lock-open' : 'fa-lock'}`} />
              <div>
                <strong>{matriculacion.activa ? 'Matriculación abierta' : 'Matriculación cerrada'}</strong>
                <span>
                  {matriculacion.activa
                    ? 'Los alumnos pueden matricularse con el código.'
                    : 'Los alumnos no pueden matricularse solos; solo vos podés inscribirlos.'}
                </span>
              </div>
              <button type="button" className="particular-secondary-btn" onClick={handleToggle} disabled={saving}>
                {matriculacion.activa ? 'Desactivar' : 'Activar'}
              </button>
            </div>

            {tieneCodigo ? (
              <div className="particular-matricula-code">
                <span>Código de matriculación</span>
                <div className="particular-matricula-code-row">
                  <code>{matriculacion.codigo}</code>
                  <button type="button" className="particular-secondary-btn" onClick={handleCopy} disabled={saving}>
                    <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`} />
                    {copied ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
                {matriculacion.generadoUtc && <small>Generado el {formatFecha(matriculacion.generadoUtc)}</small>}
              </div>
            ) : (
              <p className="particular-matricula-muted">Este curso todavía no tiene código de matriculación.</p>
            )}

            {confirmRegenerar && (
              <div className="particular-matricula-warning">
                <i className="fas fa-exclamation-triangle" />
                <span>
                  Se va a generar un código nuevo y <strong>el actual deja de funcionar</strong>. Los alumnos ya matriculados no se ven afectados.
                </span>
              </div>
            )}
          </>
        )}

        {error && <div className="particular-matricula-error">{error}</div>}

        <div className="particular-modal-footer">
          <button type="button" className="particular-secondary-btn" onClick={onClose} disabled={saving}>Cerrar</button>
          {!loading && matriculacion && (
            tieneCodigo && !confirmRegenerar ? (
              <button type="button" className="particular-secondary-btn" onClick={() => setConfirmRegenerar(true)} disabled={saving}>
                <i className="fas fa-sync-alt" />
                Generar código nuevo
              </button>
            ) : (
              <button type="button" className="particular-primary-btn" onClick={handleGenerar} disabled={saving}>
                <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-key'}`} />
                {tieneCodigo ? 'Confirmar código nuevo' : 'Generar código'}
              </button>
            )
          )}
        </div>
      </div>
    </ParticularModal>
  );
};

export default ParticularCursoMatriculacionModal;
